/**
 * A byte count for display — the ONE formatter for file sizes and storage (MAR-948).
 *
 * Binary units (1 KB = 1024 B), which is what every copy this replaced already used. Bytes and KB are
 * whole numbers; MB and up carry at most one decimal, with a trailing `.0` dropped (`25 MB`,
 * `1.5 GB`). A value that rounds up to 1024 of its unit is printed in the next one, so `1024 KB`
 * never appears. The decimal separator is `.`, matching `es-SV` and `en-US`, the app's two locales.
 *
 * In `shared/` because the server formats sizes too (the docx-service limit messages).
 * `tests/unit/formatHelpersSweep.test.ts` fails if a local copy comes back.
 */
const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

export function formatBytes(bytes: number): string {
  let value = Number.isFinite(bytes) && bytes > 0 ? bytes : 0
  let unit = 0
  while (unit < UNITS.length - 1 && round(value, unit) >= 1024) {
    value /= 1024
    unit += 1
  }
  const digits = unit < 2 ? 0 : 1
  const text = new Intl.NumberFormat('en-US', { maximumFractionDigits: digits, useGrouping: unit > 0 })
    .format(value)
  return `${text} ${UNITS[unit]}`
}

/** `value` as it will be printed in `unit`, so the unit choice and the output agree on rounding. */
function round(value: number, unit: number): number {
  const scale = unit < 2 ? 1 : 10
  return Math.round(value * scale) / scale
}
